import type { NarrationStatus, Story } from '../types';
import type { PlayerPreferences } from './types';

/** Urdu narrators (locked list); mirrors URDU_VOICES in server/src/services/narration.service.ts. */
export const URDU_VOICE_IDS = new Set(['QbjoF56fjzQK0OTI4hGt', 'aPfeouerZvEVukwmLSP0', 'XKV307Y3BonMEUh5pCNM', 'xWRIcE4FuE8s9387uHX3']);

/**
 * The narrator the listener saved for a language, or null for the server's
 * choice (their saved one, else George / Zafar). An Urdu voice saved in the
 * English slot (the older single setting) counts as the Urdu narrator.
 */
export function savedVoice(prefs: PlayerPreferences, lang: 'en' | 'ur'): string | null {
  const en = prefs.narrationVoice;
  if (lang === 'en') return en && !URDU_VOICE_IDS.has(en) ? en : null;
  if (prefs.narrationVoiceUr && URDU_VOICE_IDS.has(prefs.narrationVoiceUr)) return prefs.narrationVoiceUr;
  return en && URDU_VOICE_IDS.has(en) ? en : null;
}

/** The story's first language. Old 'mix' stories read as Urdu. */
export function primaryLanguage(story: Story | null | undefined): 'en' | 'ur' {
  return story?.storyLanguage === 'en' ? 'en' : 'ur';
}

/** Rough spoken length when the real clip length is not known yet. */
export function estimateSpeechMs(text: string): number {
  const words = text.trim().split(/\s+/).filter(Boolean).length;
  return words === 0 ? 0 : Math.round((words / 2.4) * 1000) + 400;
}

/** Clip lengths in one language from the narration status: opening, closing and per memory. */
export function clipLengths(status: NarrationStatus | null, story: Story, lang: 'en' | 'ur', voice: string | null) {
  const want = voice ?? status?.voices[lang] ?? null;
  const find = (part: 'opening' | 'closing' | 'line', mediaId?: string) =>
    status?.clips.find((c) => c.part === part && c.lang === lang && c.mediaId === mediaId && c.voice === want && c.status === 'ready');
  const ms = (part: 'opening' | 'closing' | 'line', text: string, mediaId?: string) =>
    find(part, mediaId)?.durationMs || estimateSpeechMs(text);
  return {
    voice: want,
    opening: ms('opening', story.opening[lang]),
    closing: ms('closing', story.closing[lang]),
    lines: new Map(story.lines.map((l) => [l.mediaId, ms('line', l[lang], l.mediaId)])),
    /** The clip's version for the audio URL (text + voice hash), when known. */
    version: (part: 'opening' | 'closing' | 'line', mediaId?: string) => find(part, mediaId)?.hash ?? story.generatedAt,
  };
}
